import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExamService } from '../../../Services/examServices';
import LiveExam from '../../../Services/liveexam.services';
import { errorResponseHelper } from '../../../helpers/request.helper';
import { defaultTheme, withAlpha, PageShell, TopBar, UserChip, Pill, NxButton, NxIcon } from '../../../components/nx';
import { PaymentService } from '../../../Services/paymentService';
import { AuthService } from '../../../Services/authServices';
import Auth from '../../../auth/auth';
import fingerPrint from '../../../helpers/fingerprintjs';
import AllPagesPDFViewer from '../Instructions/pdfview/AllPagesPdfViewer';
import { createGroupPaymentOrder, openGroupRazorpayCheckout } from '../../../api/razorpay';
import { ApprovalNotice } from '../../../components/ApprovalNotice/ApprovalNotice';
import makeRequest from '../../../api/makeRequest';
import url from '../../../api/urls';
import { RequstMethods } from '../../../api/requestMethode';
import { renderMarkdown } from '../../../helpers/markdownHelper';

const t = defaultTheme;
const PROOF_MAX_BYTES = 5 * 1024 * 1024;
const PROOF_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'pdf'];

export default function Dashboard() {
    const navigate = useNavigate();
    const [examGroups, setExamGroups] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [enrollingId, setEnrollingId] = useState<string | null>(null);
    const [startingId, setStartingId] = useState<string | null>(null);
    const [certificateLoading, setCertificateLoading] = useState<string | null>(null);
    const [profile, setProfile] = useState<any>();
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [instructionModal, setInstructionModal] = useState<{ examId: string; pdf: string | null; loading: boolean } | null>(null);
    const [instructionAgreed, setInstructionAgreed] = useState(false);
    const [groupPaymentModal, setGroupPaymentModal] = useState<{
        group: any;
        paying: boolean;
    } | null>(null);
    const [verifyingPayment, setVerifyingPayment] = useState(false);
    const [paymentAgreed, setPaymentAgreed] = useState(false);
    const [manualPaymentModal, setManualPaymentModal] = useState<{
        group: any;
        linkOpened: boolean;
        proofStage: boolean;
        submitting: boolean;
    } | null>(null);
    const [proofFile, setProofFile] = useState<File | null>(null);
    const [dashboardInstruction, setDashboardInstruction] = useState<{ title: string; content: string } | null>(null);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    // The instruction PDF is rendered at a fixed pixel width, so it has to track the viewport
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const pdfWidth = Math.max(240, Math.min(720, viewportWidth - 100));


    async function fetchExams() {
        try {
            const res = await ExamService.getLiveExamsWithEnrollStatus();
            if (res.status === 200) {
                const groups = res.data.data.exams || [];
                setExamGroups(groups);
                // Auto-expand all groups
                setExpandedGroups(new Set(groups.map((g: any) => g.groupId)));
            }
        } catch (err) {
            toast.error('Failed to load exams');
        } finally {
            setLoading(false);
        }
    }

    async function getProfile() {
        try {
            const res = await ExamService.getUserInfoWtihoutExams();
            if (res.status === 200) {
                setProfile(res.data.data.studentInfo[0]);
            }
        } catch (e) { }
    }

    async function fetchDashboardInstruction() {
        try {
            const res = await makeRequest(url.settings.getByKey + "/instructions", RequstMethods.GET);
            if (res.status === 200 && res.data.data) {
                const content = res.data.data.dashboardInstructionContent || "";
                if (content.trim()) {
                    setDashboardInstruction({
                        title: res.data.data.dashboardInstructionTitle || "Instructions",
                        content,
                    });
                }
            }
        } catch (e) {
            console.error("Failed to fetch dashboard instructions", e);
        }
    }

    useEffect(() => {
        fetchExams();
        getProfile();
        fetchDashboardInstruction();
    }, []);

    async function openInstructionModal(examId: string) {
        setInstructionModal({ examId, pdf: null, loading: true });
        setInstructionAgreed(false);
        try {
            const res = await ExamService.getInstructions(examId);
            if (res.status === 200) {
                const pdf = res.data.data.instructions;
                setInstructionModal({ examId, pdf: pdf || null, loading: false });
            }
        } catch (e) {
            setInstructionModal({ examId, pdf: null, loading: false });
        }
    }

    async function handleEnroll(examId: string) {
        setEnrollingId(examId);
        try {
            const res = await ExamService.selfEnroll(examId);
            if (res.status === 200) {
                toast.success('Enrolled! Payment verification pending.');
                setInstructionModal(null);
                fetchExams();
            }
        } catch (err: any) {
            errorResponseHelper(err);
        } finally {
            setEnrollingId(null);
        }
    }

    async function generateTokenForLiveExam(examId: string, mappingId: string) {
        const res = await LiveExam.generateTokenForLiveExam(examId, mappingId);
        if (res.status === 200) {
            sessionStorage.setItem('liveToken', res.data.data.token);
            toast.success('Exam countdown has begun');
            navigate('/live/exam-live', { state: { examId, mappingId } });
        }
    }

    async function handleStart(examId: string, mappingId: string) {
        setStartingId(examId);
        try {
            const res = await ExamService.startExamId(examId);
            if (res.status === 200 && res.data) {
                if (res.data.examStart === true) {
                    await generateTokenForLiveExam(examId, mappingId);
                } else {
                    navigate('/exam/instructions', { state: { examId, mappingId } });
                }
            }
        } catch (err: any) {
            toast.error(err?.response?.data || 'Could not start exam');
        } finally {
            setStartingId(null);
        }
    }

    async function handleLogout() {
        try {
            const res = await AuthService.logout({ devInfo: await fingerPrint() });
            if (res.status === 200) {
                Auth.signout();
                localStorage.setItem("authKey", res.data.data.token);
                toast.success("Logged out");
            }
            navigate('/login');
        } catch (err) {
            navigate('/login');
        }
    }

    async function handleViewCertificate(mappingId: string) {
        setCertificateLoading(mappingId);
        try {
            const res = await ExamService.getCertificate(mappingId);
            if (res.status === 200 && res.data?.certificate) {
                let pdfData: string = res.data.certificate;
                // Strip data URI prefix if present
                if (pdfData.includes(',')) {
                    pdfData = pdfData.split(',')[1];
                }
                const byteCharacters = atob(pdfData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            } else {
                toast.error('Certificate not available yet');
            }
        } catch (err: any) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Certificate not available');
        } finally {
            setCertificateLoading(null);
        }
    }



    // Open group payment modal — manual payment groups get their own flow
    function openGroupPaymentModal(group: any) {
        setPaymentAgreed(false);
        if (group.isManualPayment) {
            if (!group.paymentLink) {
                toast.error('Payment link not configured. Please contact the administrator.');
                return;
            }
            setProofFile(null);
            setManualPaymentModal({ group, linkOpened: false, proofStage: false, submitting: false });
            return;
        }
        setGroupPaymentModal({ group, paying: false });
    }

    // Manual payment: open the external payment page in a new tab
    function openManualPaymentLink() {
        if (!manualPaymentModal) return;
        window.open(manualPaymentModal.group.paymentLink, '_blank', 'noopener,noreferrer');
        setManualPaymentModal({ ...manualPaymentModal, linkOpened: true });
    }

    // Manual payment: validate and stage the proof-of-payment file
    function handleProofSelect(file: File | null) {
        if (!file) {
            setProofFile(null);
            return;
        }
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (PROOF_ALLOWED_EXTENSIONS.indexOf(ext) === -1) {
            toast.error('Proof must be an image (JPG, PNG, GIF, WEBP, BMP) or a PDF file.');
            return;
        }
        if (file.size > PROOF_MAX_BYTES) {
            toast.error('Proof must be smaller than 5 MB.');
            return;
        }
        setProofFile(file);
    }

    // Manual payment: upload the proof, then queue the payment for admin approval
    async function submitManualPayment() {
        if (!manualPaymentModal) return;
        if (!proofFile) {
            toast.error('Please attach your proof of payment.');
            return;
        }
        setManualPaymentModal({ ...manualPaymentModal, submitting: true });
        try {
            const fd = new FormData();
            fd.append('file', proofFile);
            const uploadRes = await AuthService.uploadFile(fd);
            const proofUrl = uploadRes.data?.url || uploadRes.data?.data?.fileUrl;
            if (!proofUrl) {
                toast.error('Failed to upload proof of payment. Please try again.');
                setManualPaymentModal((prev) => (prev ? { ...prev, submitting: false } : prev));
                return;
            }

            const res = await PaymentService.requestManualPayment(manualPaymentModal.group.groupId, {
                paymentProof: proofUrl,
                paymentProofName: proofFile.name,
            });
            if (res.status === 200) {
                toast.success('Payment submitted. Awaiting admin approval.');
                setManualPaymentModal(null);
                setProofFile(null);
                fetchExams();
                return;
            }
        } catch (err: any) {
            errorResponseHelper(err);
        }
        setManualPaymentModal((prev) => (prev ? { ...prev, submitting: false } : prev));
    }

    // Create group payment order and launch Razorpay checkout
    async function confirmGroupPayment() {
        if (!groupPaymentModal) return;
        setGroupPaymentModal({ ...groupPaymentModal, paying: true });
        try {
            const transaction = await createGroupPaymentOrder(groupPaymentModal.group.groupId);
            if (!transaction) {
                // Free group or already paid — just reload
                toast.success('Group access granted!');
                setGroupPaymentModal(null);
                fetchExams();
                return;
            }
            await openGroupRazorpayCheckout(
                transaction,
                {
                    name: profile?.name || 'Candidate',
                    email: profile?.email || '',
                    number: profile?.mobile || '',
                    applicationno: profile?.registrationNumber || '',
                },
                (verifying) => {
                    setVerifyingPayment(verifying);
                    if (verifying) setGroupPaymentModal(null);
                },
            );
        } catch (err: any) {
            errorResponseHelper(err);
        } finally {
            setGroupPaymentModal((prev) => (prev ? { ...prev, paying: false } : prev));
        }
    }

    const allExams = examGroups.flatMap((g: any) => g.exams || []);
    const totalExams = allExams.length;

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const profileName = profile ? `${profile.name || ''} ${profile.last_name || ''}`.trim() : 'User';
    const isApproved = profile?.isApproved === true;

    return (
        <PageShell t={t} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <TopBar
                t={t}
                crumbs={['Dashboard']}
                right={
                    <UserChip
                        t={t}
                        name={profileName}
                        email={profile?.email}
                        regNo={profile?.registrationNumber}
                        onLogout={handleLogout}
                        onProfile={() => navigate('/exam/user/profile')}
                    />
                }
            />

            <div className="nx-page-body">
                {/* Greeting */}
                <div className="nx-dash-head" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
                    <div>
                        <div style={{ fontSize: 12, color: t.inkMute, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
                            {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        <h1 className="nx-dash-title" style={{
                            // fontFamily: '"Instrument Serif", serif',
                            fontSize: 34, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.01em',
                            color: t.ink, margin: '6px 0 0',
                        }}>
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.name || 'there'}.
                        </h1>
                    </div>
                    <div className="nx-dash-stats" style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: 13 }}>
                        {[
                            { l: 'Groups', v: examGroups.length },
                            { l: 'Exams', v: totalExams },
                            { l: 'Enrolled', v: allExams.filter(e => e.isEnrolled).length },
                            { l: 'Completed', v: allExams.filter(e => e.completionStatus).length },
                        ].map((s, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span style={{ fontSize: 22, fontWeight: 500, color: t.ink, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{s.v}</span>
                                <span style={{ fontSize: 11, color: t.inkMute, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.l}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Approval pending / rejected banner */}
                {!loading && profile && !isApproved && (
                    <div style={{ marginBottom: 18 }}>
                        <ApprovalNotice t={t} profile={profile} />
                    </div>
                )}

                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 36, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 500, color: t.ink, margin: 0, letterSpacing: '-0.005em' }}>Exam Groups</h2>
                        <span style={{ fontSize: 12, color: t.inkMute }}>{examGroups.length} groups, {totalExams} exams</span>
                    </div>
                </div>

                {/* Groups */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: t.inkMute }}>Loading...</div>
                ) : examGroups.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: t.inkMute, fontSize: 13 }}>No exam groups available</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {examGroups.map((group: any) => {
                            const isExpanded = expandedGroups.has(group.groupId);
                            const exams = group.exams || [];
                            return (
                                <div key={group.groupId} style={{
                                    background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden',
                                }}>
                                    {/* Group header */}
                                    <div
                                        className="nx-group-head"
                                        style={{
                                            padding: '16px 20px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer', background: t.paperAlt,
                                            borderBottom: isExpanded ? `1px solid ${t.border}` : 'none',
                                        }}
                                        onClick={() => toggleGroup(group.groupId)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                width: 20, height: 20, fontSize: 12, color: t.inkSoft,
                                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                            }}>&#9654;</span>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 600, color: t.ink }}>{group.groupName}</div>
                                                <div style={{ fontSize: 12, color: t.inkMute, marginTop: 2 }}>
                                                    {exams.length} exam{exams.length !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} onClick={(e) => e.stopPropagation()}>
                                            {group.isGroupPaid ? (
                                                <Pill t={t} tone="enrolled">Paid</Pill>
                                            ) : group.groupPaymentStatus === 'PENDING' ? (
                                                <Pill t={t} tone="warn">Approval Pending</Pill>
                                            ) : group.groupFees > 0 ? (
                                                <>
                                                    {group.groupPaymentStatus === 'REJECTED' && (
                                                        <Pill t={t} tone="danger">Payment Rejected</Pill>
                                                    )}
                                                    <button
                                                        onClick={() => isApproved ? openGroupPaymentModal(group) : toast.error('Your registration is pending approval.')}
                                                        style={{
                                                            height: 32, padding: '0 16px', borderRadius: 7,
                                                            border: 'none', background: t.accent, color: '#fff',
                                                            fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: isApproved ? 'pointer' : 'not-allowed',
                                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                                            opacity: isApproved ? 1 : 0.6,
                                                        }}
                                                    >
                                                        {group.groupPaymentStatus === 'REJECTED' ? 'Pay again' : `Pay ₹${group.groupFees.toLocaleString('en-IN')}`} {NxIcon.arrowRight('#fff')}
                                                    </button>
                                                </>
                                            ) : (
                                                <Pill t={t} tone="enrolled">Free</Pill>
                                            )}
                                        </div>
                                    </div>

                                    {/* Exam rows inside group */}
                                    {isExpanded && (
                                        <div>
                                            {/* Column headers */}
                                            <div className="nx-exam-head" style={{
                                                display: 'grid', gridTemplateColumns: '1.6fr 110px 130px 130px',
                                                padding: '10px 20px', gap: 16,
                                                borderBottom: `1px solid ${t.border}`,
                                                fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600,
                                            }}>
                                                <span>Exam</span>
                                                <span>Subject</span>
                                                <span>Status</span>
                                                <span style={{ textAlign: 'right' }}>Action</span>
                                            </div>

                                            {exams.length === 0 ? (
                                                <div style={{ padding: '20px', textAlign: 'center', color: t.inkMute, fontSize: 13 }}>
                                                    No exams in this group
                                                </div>
                                            ) : exams.map((exam: any, i: number) => (
                                                <div key={exam._id} className="nx-exam-row" style={{
                                                    display: 'grid', gridTemplateColumns: '1.6fr 110px 130px 130px',
                                                    padding: '14px 20px', gap: 16, alignItems: 'center',
                                                    borderBottom: i < exams.length - 1 ? `1px solid ${t.border}` : 'none',
                                                }}>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: 13.5, color: t.ink, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {exam.examName}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: t.inkMute, marginTop: 2 }}>
                                                            {exam.totalQuestions || '—'} questions
                                                        </div>
                                                    </div>
                                                    <span style={{ fontSize: 13, color: t.inkSoft }}>{exam.subject || '—'}</span>
                                                    <span>
                                                        <Pill t={t} tone={exam.isEnrolled ? (exam.completionStatus ? 'neutral' : 'enrolled') : 'notenrolled'}>
                                                            {exam.completionStatus ? 'Completed' : exam.isEnrolled ? 'Enrolled' : 'Not enrolled'}
                                                        </Pill>
                                                    </span>
                                                    <span className="nx-exam-row-action" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        {renderAction(exam, group)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {dashboardInstruction && (
                <div className="nx-modal-overlay" style={{ zIndex: 9998 }}>
                    <div className="nx-modal nx-modal-lg nx-modal-tall">
                        <div className="nx-modal-head" style={{
                            padding: '18px 28px', borderBottom: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: t.paperAlt,
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>
                                {dashboardInstruction.title}
                            </h3>
                            <button
                                onClick={() => setDashboardInstruction(null)}
                                style={{
                                    background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                    padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >Close</button>
                        </div>

                        <div className="nx-modal-body" style={{ flex: 1, overflow: 'auto', padding: 28, fontSize: 14, lineHeight: 1.7, color: t.ink }}>
                            <div dangerouslySetInnerHTML={{ __html: renderMarkdown(dashboardInstruction.content) }} />
                        </div>

                        <div className="nx-modal-foot" style={{
                            padding: '14px 28px', borderTop: `1px solid ${t.border}`,
                            display: 'flex', justifyContent: 'flex-end',
                        }}>
                            <NxButton t={t} kind="primary" onClick={() => setDashboardInstruction(null)}>
                                I understand
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Instruction modal before enrol */}
            {instructionModal && (
                <div className="nx-modal-overlay">
                    <div className="nx-modal nx-modal-md">
                        <div className="nx-modal-head" style={{
                            padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Exam Instructions</h3>
                            <button
                                onClick={() => setInstructionModal(null)}
                                style={{
                                    background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                    padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >Close</button>
                        </div>

                        <div className="nx-modal-body" style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', justifyContent: 'center' }}>
                            {instructionModal.loading ? (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>Loading instructions...</div>
                            ) : instructionModal.pdf ? (
                                <AllPagesPDFViewer pdf={instructionModal.pdf} width={pdfWidth} />
                            ) : (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>No instructions available for this exam.</div>
                            )}
                        </div>

                        <div className="nx-modal-foot" style={{
                            padding: '14px 24px', borderTop: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        }}>
                            <label
                                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: t.inkSoft, cursor: 'pointer' }}
                                onClick={() => setInstructionAgreed(!instructionAgreed)}
                            >
                                <span style={{
                                    width: 16, height: 16, borderRadius: 4,
                                    background: instructionAgreed ? t.accent : 'transparent',
                                    border: instructionAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {instructionAgreed && NxIcon.check('#fff')}
                                </span>
                                I have read and understood the instructions.
                            </label>
                            <NxButton
                                t={t} kind="primary"
                                disabled={!isApproved || !instructionAgreed || enrollingId === instructionModal.examId}
                                onClick={() => isApproved ? handleEnroll(instructionModal.examId) : toast.error('Your registration is pending approval. You cannot enroll until approved by admin.')}
                            >
                                {enrollingId === instructionModal.examId ? 'Enrolling...' : !isApproved ? 'Approval Pending' : 'Proceed to Enroll'}
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Group payment confirmation modal */}
            {groupPaymentModal && (() => {
                const group = groupPaymentModal.group;
                const groupFee = group.groupFees || 0;
                const exams = group.exams || [];
                return (
                    <div className="nx-modal-overlay" onClick={() => !groupPaymentModal.paying && setGroupPaymentModal(null)}>
                        <div
                            className="nx-modal nx-modal-sm"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="nx-modal-head" style={{
                                padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Pay for Exam Group</h3>
                                <button
                                    onClick={() => setGroupPaymentModal(null)}
                                    disabled={groupPaymentModal.paying}
                                    style={{
                                        background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                        padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                    }}
                                >Close</button>
                            </div>

                            {/* Body */}
                            <div className="nx-modal-body" style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 6 }}>
                                    Exam Group
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 500, color: t.ink, lineHeight: 1.3, marginBottom: 16 }}>
                                    {group.groupName}
                                </div>

                                {/* Exams included */}
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 8 }}>
                                    Exams included ({exams.length})
                                </div>
                                <div style={{ background: t.paperAlt, borderRadius: 10, padding: '4px 16px', marginBottom: 16 }}>
                                    {exams.map((exam: any, i: number) => (
                                        <div key={exam._id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '10px 0',
                                            borderBottom: i < exams.length - 1 ? `1px solid ${t.border}` : 'none',
                                        }}>
                                            <span style={{ fontSize: 13, color: t.ink }}>{exam.examName}</span>
                                            <span style={{ fontSize: 12, color: t.inkMute }}>{exam.subject || ''}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Detail rows */}
                                <div style={{ background: t.paperAlt, borderRadius: 10, padding: '4px 16px' }}>
                                    {[
                                        { label: 'Candidate', value: profile?.name || '—' },
                                        { label: 'Registration No.', value: profile?.registrationNumber || '—' },
                                    ].map((row, i, arr) => (
                                        <div key={row.label} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                                            padding: '12px 0',
                                            borderBottom: i < arr.length - 1 ? `1px solid ${t.border}` : 'none',
                                        }}>
                                            <span style={{ fontSize: 13, color: t.inkSoft }}>{row.label}</span>
                                            <span style={{ fontSize: 13, color: t.ink, fontWeight: 500, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Amount */}
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                    marginTop: 20, paddingTop: 16, borderTop: `1px solid ${t.border}`,
                                }}>
                                    <span style={{ fontSize: 14, color: t.inkSoft }}>Amount to pay</span>
                                    <span style={{ fontSize: 26, fontWeight: 600, color: t.ink, letterSpacing: '-0.01em' }}>
                                        ₹{groupFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>

                                {/* Agree checkbox */}
                                <label
                                    onClick={() => setPaymentAgreed(v => !v)}
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer' }}
                                >
                                    <span style={{
                                        width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                                        background: paymentAgreed ? t.accent : 'transparent',
                                        border: paymentAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {paymentAgreed && NxIcon.check('#fff')}
                                    </span>
                                    <span style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>
                                        I agree to pay for this exam group and understand that this gives me access to enroll in all exams within this group.
                                    </span>
                                </label>
                            </div>

                            {/* Footer */}
                            <div className="nx-modal-foot" style={{ padding: '14px 24px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <NxButton t={t} kind="secondary" disabled={groupPaymentModal.paying} onClick={() => setGroupPaymentModal(null)}>
                                    Cancel
                                </NxButton>
                                <NxButton
                                    t={t} kind="primary"
                                    disabled={groupPaymentModal.paying || !paymentAgreed}
                                    onClick={confirmGroupPayment}
                                >
                                    {groupPaymentModal.paying
                                        ? 'Opening…'
                                        : `Pay ₹${groupFee.toLocaleString('en-IN')}`}
                                </NxButton>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Manual payment modal */}
            {manualPaymentModal && (() => {
                const group = manualPaymentModal.group;
                const groupFee = group.groupFees || 0;
                const busy = manualPaymentModal.submitting;
                const notes = [
                    'Do not repeat this payment — pay only once for this exam group.',
                    'Click "Proceed to Payment" to open the payment page and complete your payment there.',
                    'After paying, attach a screenshot or receipt of the payment as proof (image or PDF, up to 5 MB) and click "I have completed the payment".',
                    'Your access will be activated once an admin verifies your proof and approves the payment.',
                    'Approval can take some time. Please do not pay again while you are waiting.',
                ];
                return (
                    <div className="nx-modal-overlay" onClick={() => !busy && setManualPaymentModal(null)}>
                        <div
                            className="nx-modal nx-modal-sm"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="nx-modal-head" style={{
                                padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Pay for Exam Group</h3>
                                <button
                                    onClick={() => setManualPaymentModal(null)}
                                    disabled={busy}
                                    style={{
                                        background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                        padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                    }}
                                >Close</button>
                            </div>

                            {/* Body */}
                            <div className="nx-modal-body" style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 6 }}>
                                    Exam Group
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 500, color: t.ink, lineHeight: 1.3, marginBottom: 16 }}>
                                    {group.groupName}
                                </div>

                                {/* Detail rows */}
                                <div style={{ background: t.paperAlt, borderRadius: 10, padding: '4px 16px' }}>
                                    {[
                                        { label: 'Candidate', value: profile?.name || '—' },
                                        { label: 'Registration No.', value: profile?.registrationNumber || '—' },
                                        { label: 'Amount', value: `₹${groupFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },
                                    ].map((row, i, arr) => (
                                        <div key={row.label} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                                            padding: '12px 0',
                                            borderBottom: i < arr.length - 1 ? `1px solid ${t.border}` : 'none',
                                        }}>
                                            <span style={{ fontSize: 13, color: t.inkSoft }}>{row.label}</span>
                                            <span style={{ fontSize: 13, color: t.ink, fontWeight: 500, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Important notes */}
                                <div style={{
                                    marginTop: 20, padding: '14px 16px', borderRadius: 10,
                                    background: withAlpha(t.warning, 0.08),
                                    border: `1px solid ${withAlpha(t.warning, 0.25)}`,
                                }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.warning, marginBottom: 8 }}>
                                        Please read before paying
                                    </div>
                                    {notes.map((note) => (
                                        <div key={note} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                                            <span style={{ color: t.warning, fontSize: 13, lineHeight: 1.5 }}>&bull;</span>
                                            <span style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>{note}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Agree checkbox */}
                                <label
                                    onClick={() => setPaymentAgreed(v => !v)}
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer' }}
                                >
                                    <span style={{
                                        width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                                        background: paymentAgreed ? t.accent : 'transparent',
                                        border: paymentAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {paymentAgreed && NxIcon.check('#fff')}
                                    </span>
                                    <span style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>
                                        I have read and understood the above.
                                    </span>
                                </label>

                                {manualPaymentModal.linkOpened && !manualPaymentModal.proofStage && (
                                    <div style={{ fontSize: 12, color: t.success, lineHeight: 1.5, marginTop: 14 }}>
                                        Payment page opened in a new tab. Once you have paid, click "I have completed the
                                        payment". If the page did not open, allow pop-ups for this site and try again.
                                    </div>
                                )}

                                {/* Proof of payment — revealed once the candidate says they have paid */}
                                {manualPaymentModal.proofStage && (
                                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${t.border}` }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 8 }}>
                                        Proof of payment
                                    </div>
                                    <div style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5, marginBottom: 10 }}>
                                        Attach a screenshot or receipt of your payment. An admin will review this before
                                        approving your access. Image or PDF, up to 5 MB.
                                    </div>
                                    <label
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                                            background: t.paperAlt,
                                            border: `1px dashed ${proofFile ? t.accent : t.borderStrong}`,
                                        }}
                                    >
                                        <input
                                            type="file"
                                            accept=".png,.jpg,.jpeg,.gif,.webp,.bmp,.pdf"
                                            style={{ display: 'none' }}
                                            onChange={(e) => handleProofSelect(e.target.files?.[0] || null)}
                                        />
                                        <span style={{
                                            height: 30, padding: '0 12px', borderRadius: 7, flexShrink: 0,
                                            border: `1px solid ${t.borderStrong}`, background: t.surface, color: t.ink,
                                            fontSize: 12, fontWeight: 500,
                                            display: 'inline-flex', alignItems: 'center',
                                        }}>
                                            {proofFile ? 'Change file' : 'Choose file'}
                                        </span>
                                        <span style={{
                                            fontSize: 13, color: proofFile ? t.ink : t.inkMute,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {proofFile ? proofFile.name : 'No file attached'}
                                        </span>
                                    </label>
                                </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="nx-modal-foot" style={{ padding: '14px 24px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <NxButton t={t} kind="secondary" disabled={busy} onClick={() => setManualPaymentModal(null)}>
                                    Cancel
                                </NxButton>
                                {manualPaymentModal.proofStage ? (
                                    <NxButton
                                        t={t}
                                        kind="primary"
                                        disabled={busy || !proofFile}
                                        onClick={submitManualPayment}
                                    >
                                        {busy ? 'Uploading…' : 'Upload Proof'}
                                    </NxButton>
                                ) : (
                                    <>
                                        <NxButton
                                            t={t}
                                            kind="secondary"
                                            disabled={!paymentAgreed}
                                            onClick={openManualPaymentLink}
                                        >
                                            Proceed to Payment
                                        </NxButton>
                                        <NxButton
                                            t={t}
                                            kind="primary"
                                            disabled={!paymentAgreed}
                                            onClick={() => setManualPaymentModal({ ...manualPaymentModal, proofStage: true })}
                                        >
                                            I have completed the payment
                                        </NxButton>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Payment verification loader */}
            {verifyingPayment && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 10000,
                    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <style>{`@keyframes nx-spin { to { transform: rotate(360deg); } }`}</style>
                    <div style={{
                        background: t.surface, borderRadius: 14, padding: '32px 40px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', maxWidth: 320, textAlign: 'center',
                    }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: '50%',
                            border: `3px solid ${t.border}`, borderTopColor: t.accent,
                            animation: 'nx-spin 0.8s linear infinite',
                        }} />
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: t.ink }}>Verifying payment</div>
                            <div style={{ fontSize: 13, color: t.inkMute, marginTop: 4, lineHeight: 1.5 }}>
                                Please wait and do not close this window.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageShell>
    );

    function renderAction(exam: any, group: any) {
        if (!exam.isEnrolled) {
            if (!group.isGroupPaid) {
                return (
                    <span style={{ fontSize: 11, color: t.inkMute, whiteSpace: 'nowrap' }}>
                        {group.groupPaymentStatus === 'PENDING' ? 'Awaiting approval' : 'Pay for group first'}
                    </span>
                );
            }
            return (
                <button
                    onClick={() => openInstructionModal(exam._id)}
                    style={{
                        height: 32, padding: '0 14px', borderRadius: 7,
                        border: `1px solid ${t.borderStrong}`, background: 'transparent',
                        color: t.ink,
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
                        cursor: 'pointer',
                    }}
                >
                    Enroll
                </button>
            );
        }

        if (exam.completionStatus) {
            return (
                <div style={{ display: 'flex', gap: 6 }}>
                    <button
                        disabled
                        style={{
                            height: 32, padding: '0 14px', borderRadius: 7,
                            border: `1px solid ${t.border}`, background: 'transparent', color: t.inkMute,
                            fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'not-allowed',
                        }}
                    >
                        Completed
                    </button>
                    {exam.mappingId && (
                        <button
                            onClick={() => handleViewCertificate(exam.mappingId)}
                            disabled={certificateLoading === exam.mappingId}
                            style={{
                                height: 32, padding: '0 14px', borderRadius: 7,
                                border: `1px solid ${t.accent}`, background: 'transparent', color: t.accent,
                                fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                opacity: certificateLoading === exam.mappingId ? 0.5 : 1,
                            }}
                        >
                            {certificateLoading === exam.mappingId ? 'Loading...' : 'Certificate'}
                        </button>
                    )}
                </div>
            );
        }

        if (!group.isGroupPaid) {
            return (
                <span
                    title="Your payment for this exam group is awaiting approval. Your saved progress is safe and you can resume once it is approved."
                    style={{ fontSize: 11, color: t.warning, whiteSpace: 'nowrap' }}
                >
                    Payment required
                </span>
            );
        }

        if (!exam.mappingActive) {
            return (
                <span
                    title="Your exam access has not been activated by the invigilator yet."
                    style={{ fontSize: 11, color: t.inkMute, whiteSpace: 'nowrap' }}
                >
                    Awaiting activation
                </span>
            );
        }

        return (
            <button
                onClick={() => handleStart(exam._id, exam.mappingId)}
                disabled={startingId === exam._id}
                style={{
                    height: 32, padding: '0 14px', borderRadius: 7,
                    border: 'none', background: t.ink, color: t.paper,
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    opacity: startingId === exam._id ? 0.5 : 1,
                }}
            >
                {startingId === exam._id ? 'Starting...' : <>Continue {NxIcon.arrowRight(t.paper)}</>}
            </button>
        );
    }
}
